//* Libraries imports
import React, { useState, useRef, useEffect } from "react";
import { Lock } from "lucide-react";

//* Hooks Imports
import { useValidateSaleToken } from "@/hooks/sales/mutations/useValidateSaleToken";
import { useParams } from "react-router-dom";

type TokenValidationScreenProps = {
  onSuccess: (token: string) => void;
};

export function TokenValidationScreen({
  onSuccess,
}: TokenValidationScreenProps) {
  const [token, setToken] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const mutationValidateToken = useValidateSaleToken();
  const params = useParams();

  const handleChange = (value: string, index: number) => {
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!cleanValue && value !== "") return;

    const newToken = [...token];
    newToken[index] = cleanValue.slice(-1);
    setToken(newToken);
    localStorage.setItem(params.id,JSON.stringify(newToken));

    if (cleanValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !token[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const tokenString = token.join("");
    if (tokenString.length < 6) return;

    mutationValidateToken.mutate(
      { token: tokenString },
      {
        onSuccess: () => {
          onSuccess(tokenString);
        },
        onError: (err) => {
          console.error("Erro ao validar token:", err);
        },
      },
    );
  };

  useEffect(() => {
    if (token.every((digit) => digit !== "")) {
      handleVerify();
    }
  }, [token]);

  useEffect(()=>{
    let tokens_ = localStorage.getItem(params.id);
    if(tokens_){
      try{
      tokens_=JSON.parse(tokens_);
      let index_select=0;
      tokens_?.map((item,index)=>{
        if(item){
          index_select=index;
        }
      })
      setToken(tokens_);
      if (index_select < 5) {
        inputRefs.current[index_select + 1]?.focus();
      }
    }catch(e){

    }
    }
  },[])

  return (
    <div className="flex-1 flex flex-col bg-clarobg-202 min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md bg-clarofundo-01 rounded-3xl shadow-sombra-01 p-8 flex flex-col items-center gap-6">
        <div className="size-20 bg-blue-50 rounded-full flex items-center justify-center text-claroprimaria-01">
          <Lock size={32} />
        </div>

        <div className="text-center flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-clarotextos-01 font-inter-26-semi-bold">
            Preencha o Código
          </h1>
          <p className="text-sm text-clarotextos-02 font-inter-14-regular">
            Enviamos um código por Whatsapp
          </p>
        </div>

        <div className="flex gap-2 md:gap-3 justify-center">
          {token.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="size-11 md:size-14 text-center text-xl font-semibold border border-clarobordas-01 rounded-xl bg-clarofundo-01 text-clarotextos-01 focus:border-claroprimaria-01 focus:ring-1 focus:ring-claroprimaria-01 outline-none transition-all uppercase"
            />
          ))}
        </div>

        {mutationValidateToken.error && (
          <p className="text-red-500 text-sm text-center">
            {mutationValidateToken.error.message}
          </p>
        )}

        <div className="w-full bg-yellow-50 border border-yellow-100 p-4 rounded-2xl flex flex-col gap-1">
          <p className="text-sm text-yellow-800 font-semibold font-inter-14-semi-bold">
            Atenção à validade
          </p>
          <p className="text-xs text-yellow-700 font-inter-14-regular">
            Este código é válido por 24 horas a partir do momento que foi
            recebido. Caso tenha expirado, entre em contato com o vendedor.
          </p>
        </div>

        <div className="w-full flex flex-col pt-2">
          <button
            onClick={handleVerify}
            disabled={
              mutationValidateToken.isPending ||
              token.some((digit) => digit === "")
            }
            className="w-full bg-claroprimaria-01 hover:opacity-90 disabled:opacity-50 text-white font-semibold py-4 rounded-2xl transition-all shadow-sm font-inter-16-semi-bold"
          >
            {mutationValidateToken.isPending ? "Verificando..." : "Verificar"}
          </button>
        </div>
      </div>
    </div>
  );
}
